/**
 * What a comment may hold, in one place for the server that enforces it and the
 * screen that tells people before they hit it (#448).
 */

export const MAX_COMMENT_LENGTH = 5000;
/** Per comment. A photo of a damaged parcel or two, an invoice: not an album. */
export const MAX_COMMENT_FILES = 5;
/** Per file. Smaller than a Docs attachment's 25 MB: this is a thread, not a library. */
export const MAX_COMMENT_FILE_BYTES = 10 * 1024 * 1024;
/** Per comment: a thread that tags the whole team is a different message. */
export const MAX_COMMENT_MENTIONS = 20;

/**
 * Whether a character is one a comment cannot hold: a control character other
 * than tab, line feed and carriage return, or DEL. A NUL is the one Postgres
 * refuses outright (it cannot be stored in text), which used to surface as a
 * 500; the rest are never something a person typed on purpose and would only
 * show as boxes or garble a terminal that reads the data.
 */
function isUnstorable(code: number): boolean {
  return (
    code === 0x7f ||
    (code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d)
  );
}

export function hasUnstorableCharacter(text: string): boolean {
  for (let index = 0; index < text.length; index += 1) {
    if (isUnstorable(text.charCodeAt(index))) return true;
  }
  return false;
}

/** A file name with the characters a comment cannot hold removed. */
export function withoutUnstorableCharacters(text: string): string {
  return Array.from(text)
    .filter((char) => !isUnstorable(char.charCodeAt(0)))
    .join("");
}

export type CommentFileKind =
  "image/png" | "image/jpeg" | "image/gif" | "image/webp" | "application/pdf";

/** What a file picker's `accept` should offer. The server decides from the bytes. */
export const COMMENT_FILE_ACCEPT =
  "image/png,image/jpeg,image/gif,image/webp,application/pdf";

/**
 * What a file is, from its own first bytes — never from the type the browser
 * sent. A comment takes images and PDFs only, and trusting a claimed type would
 * let anything through under an image's name. The type a comment stores, and
 * later sends on download, is this one.
 *
 * SVG is deliberately not here: it is an image that can carry script.
 */
export function sniffCommentFileKind(
  bytes: Uint8Array,
): CommentFileKind | null {
  const startsWith = (...signature: number[]) =>
    signature.every((byte, index) => bytes[index] === byte);

  if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) {
    return "image/png";
  }
  if (startsWith(0xff, 0xd8, 0xff)) return "image/jpeg";
  // `GIF87a` or `GIF89a`.
  if (
    startsWith(0x47, 0x49, 0x46, 0x38) &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  ) {
    return "image/gif";
  }
  // `RIFF`, four size bytes, then `WEBP`.
  if (
    startsWith(0x52, 0x49, 0x46, 0x46) &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  // `%PDF-`.
  if (startsWith(0x25, 0x50, 0x44, 0x46, 0x2d)) return "application/pdf";
  return null;
}
